/**
 * The launch screen (tasks 13.5, 13.6, 13.7).
 *
 * Three environments down, three presets across, with an application selector and
 * a Watch toggle applying to whichever button is pressed.
 *
 * A refused action is DISABLED WITH ITS REASON VISIBLE rather than hidden. Hiding
 * it invites "why can't I run regression against prod?" and leaves the operator to
 * guess. The reason text comes from the server, so it cannot drift from the rule.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, type AppDecl, type EnvironmentName, type MatrixRow, type RunSummary } from '../api';

const ENVIRONMENT: Record<EnvironmentName, { label: string; blurb: string; tone: string }> = {
  dev:  { label: 'Development', blurb: 'First to receive changes. Safe to write to.', tone: 'hsl(var(--info))' },
  qa:   { label: 'QA',          blurb: 'The seeded automation tenant lives here.',    tone: 'hsl(var(--accent))' },
  prod: { label: 'Production',  blurb: 'Real customer data. Read-only packs only.',   tone: 'hsl(var(--fail))' },
};

const PRESET_ICON: Record<string, string> = {
  smoke: '◈', unit: '◇', regression: '◆', performance: '◐', security: '⛨', visual: '◫',
};

interface Props {
  onRunStarted: (run: RunSummary) => void;
}

export function RunMatrix({ onRunStarted }: Props) {
  const [rows, setRows] = useState<MatrixRow[]>([]);
  const [applications, setApplications] = useState<AppDecl[]>([]);
  const [scope, setScope] = useState('all');
  const [watch, setWatch] = useState(false);
  const [screenshots, setScreenshots] = useState(false);
  /**
   * Unit-economics inputs for a Performance run, behind a toggle.
   *
   * Behind a toggle because they are the only inputs the console cannot derive, and a
   * cost-per-request computed from a guessed monthly spend is a confident fabrication.
   * Empty means the report omits the section and lists what it would need — which is
   * more useful than a plausible wrong number.
   */
  const [costOpen, setCostOpen] = useState(false);
  const [costMonthly, setCostMonthly] = useState('');
  const [costRequestsPerUser, setCostRequestsPerUser] = useState('');
  const [costUtilisation, setCostUtilisation] = useState('0.5');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api.matrix()
      .then((m) => { setRows(m.matrix); setApplications(m.applications); })
      .catch((e: ApiError) => setNotice({ kind: 'error', text: e.message }));
  }, []);

  const scopeLabel = useMemo(
    () => applications.find((a) => a.scope === scope)?.label ?? 'Whole platform',
    [applications, scope],
  );

  async function launch(environment: EnvironmentName, preset: string, watchable: boolean) {
    const key = `${environment}:${preset}`;
    setBusy(key);
    setNotice(null);
    try {
      const result = await api.start({
        environment,
        preset: preset as never,
        scope,
        watch: watch && watchable,
        // Only sent when ticked; otherwise the per-preset default applies.
        ...(screenshots ? { screenshots: true } : {}),
        // Only for the preset that can use them, and only when supplied. The server
        // ignores them for every other preset.
        ...(preset === 'performance' && costMonthly.trim()
          ? {
              cost: {
                monthlyUsd: costMonthly.trim(),
                requestsPerUser: costRequestsPerUser.trim() || undefined,
                utilisation: costUtilisation.trim() || undefined,
              },
            }
          : {}),
      });
      if (result.queued) {
        setNotice({
          kind: 'info',
          text: result.reason ?? 'Queued behind another run.',
        });
      }
      onRunStarted(result.run);
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Scope and watch apply to every button below, so they sit above them.
          One compact toolbar row rather than loosely wrapped controls — each
          option is a small labelled unit so the row reads as a single control
          strip instead of a stack of unrelated form fields. */}
      <div className="surface flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <label htmlFor="scope" className="label m-0 whitespace-nowrap">Run against</label>
          <select
            id="scope"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="field !w-auto min-w-[11rem] !py-1.5 text-sm"
            data-testid="scope-select"
          >
            {applications.map((a) => <option key={a.scope} value={a.scope}>{a.label}</option>)}
          </select>
        </div>

        <span className="h-6 w-px" style={{ background: 'hsl(var(--border))' }} aria-hidden />

        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={watch}
            onChange={(e) => setWatch(e.target.checked)}
            data-testid="watch-toggle"
            className="h-3.5 w-3.5 rounded"
          />
          <span className="font-medium">Watch it run</span>
          <span className="hidden sm:inline" style={{ color: 'hsl(var(--text-faint))' }}>
            · stream the live browser
          </span>
        </label>

        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={screenshots}
            onChange={(e) => setScreenshots(e.target.checked)}
            data-testid="screenshots-toggle"
            className="h-3.5 w-3.5 rounded"
          />
          <span className="font-medium">Screenshot every test</span>
          <span className="hidden sm:inline" style={{ color: 'hsl(var(--text-faint))' }}>
            · otherwise, failures only
          </span>
        </label>

        {/* Advanced: the inputs for unit economics on a Performance run. Collapsed by
            default — they are irrelevant to every other preset, and an always-visible
            cost form invites someone to fill it in with a guess. */}
        <button
          type="button"
          onClick={() => setCostOpen((open) => !open)}
          data-testid="cost-toggle"
          className="ml-auto flex items-center gap-1 text-xs font-medium"
          style={{ color: costOpen ? 'hsl(var(--accent))' : 'hsl(var(--text-faint))' }}
        >
          <span className="inline-block transition-transform" style={{ transform: costOpen ? 'rotate(90deg)' : undefined }}>▸</span>
          Cost per request (Performance)
        </button>

        {costOpen && (
          <div
            className="surface-raised grid w-full grid-cols-1 gap-x-6 gap-y-3 p-3.5 sm:grid-cols-2"
            data-testid="cost-inputs"
          >
            <p className="col-span-full text-xs leading-relaxed" style={{ color: 'hsl(var(--text-faint))' }}>
              Divides your spend by the throughput the run actually demonstrates. Leave blank
              and the report omits unit economics rather than estimating them.
            </p>

            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Infra cost / month (USD)</span>
              <input
                type="number" min="0" step="any"
                value={costMonthly}
                onChange={(e) => setCostMonthly(e.target.value)}
                data-testid="cost-monthly"
                placeholder="e.g. 240"
                className="field !w-28 !py-1 text-right text-sm"
              />
            </label>

            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Requests / user / month</span>
              <input
                type="number" min="0" step="any"
                value={costRequestsPerUser}
                onChange={(e) => setCostRequestsPerUser(e.target.value)}
                data-testid="cost-requests-per-user"
                placeholder="optional"
                className="field !w-28 !py-1 text-right text-sm"
              />
            </label>

            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Planned utilisation (0–1)</span>
              <input
                type="number" min="0" max="1" step="0.05"
                value={costUtilisation}
                onChange={(e) => setCostUtilisation(e.target.value)}
                data-testid="cost-utilisation"
                className="field !w-28 !py-1 text-right text-sm"
              />
            </label>

            <p className="col-span-full text-xs leading-relaxed" style={{ color: 'hsl(var(--text-faint))' }}>
              0.5 means you plan to sit at half the measured sustainable rate — assuming 1.0
              would halve the apparent cost per request and flatter the system.
            </p>
          </div>
        )}
      </div>

      {notice && (
        <div
          role="status"
          data-testid="matrix-notice"
          className="surface flex items-start gap-3 px-4 py-2.5 text-sm"
          style={{
            borderColor: notice.kind === 'error' ? 'hsl(var(--fail) / 0.4)' : 'hsl(var(--warn) / 0.4)',
            background: notice.kind === 'error' ? 'hsl(var(--fail) / 0.08)' : 'hsl(var(--warn) / 0.08)',
          }}
        >
          <span style={{ color: notice.kind === 'error' ? 'hsl(var(--fail))' : 'hsl(var(--warn))' }}>
            {notice.kind === 'error' ? '✕' : '⏸'}
          </span>
          <p className="leading-relaxed">{notice.text}</p>
        </div>
      )}

      {rows.map((row) => {
        const env = ENVIRONMENT[row.environment];
        return (
          <section
            key={row.environment}
            className="surface overflow-hidden"
            data-testid={`env-${row.environment}`}
            style={{ borderLeft: `3px solid ${env.tone}` }}
          >
            <div
              className="flex items-center gap-2.5 px-4 py-3"
              style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--bg-overlay) / 0.5)' }}
            >
              <span className="dot" style={{ background: env.tone }} />
              <h2 className="text-base font-semibold">{env.label}</h2>
              {row.environment === 'prod' && <span className="pill pill-fail">Read-only</span>}
              <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{env.blurb}</span>
            </div>

            {/* Exactly three per row at every width the cards are legible at — a
                fixed column count keeps every card the same size instead of the
                last one in an uneven row stretching to fill the gap. Cards no
                longer clamp their text, so rows can be uneven heights; `items-start`
                keeps each card sized to its own content instead of every card in the
                row stretching to match the tallest. */}
            <div className="grid grid-cols-1 items-start gap-3.5 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {row.buttons.map((button) => {
                const allowed = button.decision.allowed;
                const comingSoon = !allowed && button.decision.reason?.startsWith('Coming soon');
                const key = `${row.environment}:${button.target.preset}`;

                return (
                  <div
                    key={key}
                    className="surface-raised flex flex-col gap-2.5 p-4 transition-shadow"
                    style={{
                      opacity: allowed ? 1 : 0.75,
                      // A dashed border for "not built yet" reads differently at a
                      // glance from the solid border everywhere else — the card is
                      // pending, not refused.
                      ...(comingSoon ? { borderStyle: 'dashed' } : {}),
                      boxShadow: allowed ? '0 1px 2px hsl(var(--bg) / 0.4)' : undefined,
                    }}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm"
                        style={{
                          background: allowed ? 'hsl(var(--accent) / 0.14)' : 'hsl(var(--text-faint) / 0.12)',
                          color: allowed ? 'hsl(var(--accent))' : 'hsl(var(--text-faint))',
                        }}
                        aria-hidden
                      >
                        {PRESET_ICON[button.target.preset]}
                      </span>
                      <span className="text-sm font-semibold">{button.label}</span>
                      {allowed && (
                        <span className="ml-auto shrink-0 text-xs tabular" style={{ color: 'hsl(var(--text-faint))' }}>
                          {button.expectedDuration}
                        </span>
                      )}
                    </div>

                    {/* Full text, not clamped: a card that says "..." with the rest
                        only on hover is not read, it is guessed at. Cards vary in
                        height now — that is the cost of every word being visible,
                        and it is a better trade than hiding half of them. */}
                    <p className="text-xs leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
                      {button.description}
                    </p>

                    <button
                      disabled={!allowed || busy !== null}
                      onClick={() => launch(row.environment, button.target.preset, button.watch.allowed)}
                      data-testid={`run-${key}`}
                      className={`btn ${button.target.preset === 'regression' ? 'btn-primary' : 'btn-secondary'} mt-auto w-full !py-1.5 text-sm`}
                    >
                      {busy === key
                        ? 'Starting…'
                        : allowed
                          ? `Run — ${scopeLabel}`
                          : comingSoon
                            ? 'Coming soon'
                            : 'Not available'}
                    </button>

                    {/* The refusal carries the SERVER'S OWN reason, not a generic
                        label — "refused by policy" tells an operator nothing and
                        invites them to ask why. Shown in full, same reasoning as
                        the description above. */}
                    {!allowed && (
                      <p
                        className="rounded-md p-2 text-xs leading-relaxed"
                        data-testid={`refusal-${key}`}
                        style={
                          comingSoon
                            ? { color: 'hsl(var(--text-muted))', background: 'hsl(var(--bg) / 0.6)' }
                            : { color: 'hsl(var(--fail))', background: 'hsl(var(--fail) / 0.08)' }
                        }
                      >
                        {button.decision.reason}
                      </p>
                    )}
                    {allowed && watch && !button.watch.allowed && (
                      <p className="text-xs" style={{ color: 'hsl(var(--warn))' }}>{button.watch.reason}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
